import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import {
  Inbox, Mail, Phone, ExternalLink, Globe, Clock, CheckCircle2,
  AlertCircle, Loader2, RefreshCw, Send, Trash2, Copy, Filter, BarChart3,
} from "lucide-react";
import RoiDashboard from "../components/RoiDashboard";

const STATUS_BADGE = {
  queued: { bg: "#fef3c7", color: "#92400e", label: "In coda" },
  analyzing: { bg: "#dbeafe", color: "#1e3a8a", label: "Analisi…" },
  completed: { bg: "#dcfce7", color: "#15803d", label: "Completata" },
  email_sent: { bg: "#dcfce7", color: "#15803d", label: "Email inviata" },
  failed: { bg: "#fee2e2", color: "#991b1b", label: "Fallita" },
};

const SOURCE_LABEL = {
  "maxvalle.it": "maxvalle.it",
  "sitidaincubo.it": "sitidaincubo.it",
  "google_ads": "Google Ads",
  "facebook_ads": "Facebook Ads",
  "linkedin_ads": "LinkedIn Ads",
  "direct": "Diretto",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso.slice(0, 16); }
}

export default function LeadInbound() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [busy, setBusy] = useState({});  // {reqId: "resending"|"deleting"}
  const [showWebhookHelp, setShowWebhookHelp] = useState(false);
  const [tab, setTab] = useState("requests");  // "requests" | "roi"

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/inbound/requests"),
        api.get("/inbound/stats"),
      ]);
      setItems(r1.data.items || []);
      setStats(r2.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh queue while analyses in progress
  useEffect(() => {
    if (!items.some(i => i.status === "queued" || i.status === "analyzing")) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [items]);

  const filtered = useMemo(() => {
    if (filterStatus === "all") return items;
    if (filterStatus === "suspicious") return items.filter(i => i.quality?.suspicious);
    return items.filter(i => i.status === filterStatus);
  }, [items, filterStatus]);

  const resend = async (reqId) => {
    if (!confirm("Reinvia la mail con il report al richiedente?")) return;
    setBusy(b => ({ ...b, [reqId]: "resending" }));
    try {
      await api.post(`/inbound/requests/${reqId}/resend`);
      await load();
    } catch (e) {
      alert("Errore reinvio: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(b => ({ ...b, [reqId]: undefined })); }
  };

  const reanalyze = async (reqId) => {
    if (!confirm("Rianalizzare questo sito con i dati aggiornati?\n\nIl link pubblico resta lo stesso e NON viene inviata nessuna email al cliente.")) return;
    setBusy(b => ({ ...b, [reqId]: "reanalyzing" }));
    try {
      await api.post(`/inbound/requests/${reqId}/reanalyze`);
      await load();
    } catch (e) {
      alert("Errore rianalisi: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(b => ({ ...b, [reqId]: undefined })); }
  };

  const reanalyzeAll = async () => {
    if (!confirm(`Rianalizzare tutti i ${items.length} lead inbound?\n\nI link pubblici restano identici e NON viene inviata nessuna email.\nOgni sito consuma crediti DataForSEO/SEOZoom.`)) return;
    setBusy(b => ({ ...b, __all: "reanalyzing" }));
    try {
      const r = await api.post("/inbound/requests/reanalyze-all");
      alert(`Rianalisi avviata per ${r.data.queued} lead. Aggiorno la lista mentre procede.`);
      await load();
    } catch (e) {
      alert("Errore rianalisi massiva: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(b => ({ ...b, __all: undefined })); }
  };

  const remove = async (reqId) => {
    if (!confirm("Eliminare definitivamente questa richiesta? L'azienda e l'analisi non saranno toccate.")) return;
    setBusy(b => ({ ...b, [reqId]: "deleting" }));
    try {
      await api.delete(`/inbound/requests/${reqId}`);
      setItems(items.filter(i => i.id !== reqId));
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(b => ({ ...b, [reqId]: undefined })); }
  };

  return (
    <div data-testid="lead-inbound-page">
      <div className="flex items-start gap-3 mb-6">
        <Inbox size={28} color="var(--mv-primary)" />
        <div style={{flex:1}}>
          <div className="h-display">Lead Inbound</div>
          <div className="text-muted mt-1">
            Richieste di analisi gratuita ricevute dai form pubblici su <strong>maxvalle.it</strong> e <strong>sitidaincubo.it</strong>.
            L'analisi viene avviata in automatico; il report viene inviato via email al richiedente appena pronto.
          </div>
        </div>
        <button onClick={reanalyzeAll} disabled={!items.length || busy.__all === "reanalyzing"}
          className="btn btn-ghost btn-sm" data-testid="btn-reanalyze-all"
          title="Rianalizza tutti i lead con i dati aggiornati (nessuna email inviata)">
          {busy.__all === "reanalyzing" ? <Loader2 size={14} className="spin"/> : <RefreshCw size={14}/>} Rianalizza tutti
        </button>
        <button onClick={()=>setShowWebhookHelp(s=>!s)} className="btn btn-ghost btn-sm" data-testid="webhook-help-btn">
          <Copy size={14}/> Configurazione webhook
        </button>
      </div>

      {showWebhookHelp && <WebhookHelpCard onClose={()=>setShowWebhookHelp(false)} />}

      {/* Tabs */}
      <div className="flex gap-2 mb-4" style={{borderBottom: "1px solid #eee"}}>
        <button onClick={()=>setTab("requests")} data-testid="tab-requests"
          style={{
            padding: "10px 18px", background: "transparent", border: "none",
            fontWeight: 600, fontSize: 14, cursor: "pointer",
            color: tab === "requests" ? "var(--mv-primary)" : "var(--mv-text-muted)",
            borderBottom: tab === "requests" ? "2px solid var(--mv-primary)" : "2px solid transparent",
            marginBottom: -1, display: "flex", alignItems: "center", gap: 6,
          }}>
          <Inbox size={15}/> Richieste
        </button>
        <button onClick={()=>setTab("roi")} data-testid="tab-roi"
          style={{
            padding: "10px 18px", background: "transparent", border: "none",
            fontWeight: 600, fontSize: 14, cursor: "pointer",
            color: tab === "roi" ? "var(--mv-primary)" : "var(--mv-text-muted)",
            borderBottom: tab === "roi" ? "2px solid var(--mv-primary)" : "2px solid transparent",
            marginBottom: -1, display: "flex", alignItems: "center", gap: 6,
          }}>
          <BarChart3 size={15}/> Dashboard ROI Sorgenti
        </button>
      </div>

      {tab === "roi" ? <RoiDashboard/> : (
      <>
      {/* KPI tiles */}
      <div className="grid gap-3 mb-5" style={{gridTemplateColumns:"repeat(auto-fit, minmax(170px,1fr))"}}>
        <div className="card" style={{padding:14}}>
          <div className="stat-label">Totale richieste</div>
          <div className="stat-num" data-testid="kpi-total">{stats?.total ?? "—"}</div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label">In corso</div>
          <div className="stat-num" style={{color:"#1e3a8a"}} data-testid="kpi-pending">
            {(stats?.by_status?.queued || 0) + (stats?.by_status?.analyzing || 0)}
          </div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label">Email inviate</div>
          <div className="stat-num" style={{color:"var(--mv-success)"}} data-testid="kpi-sent">
            {stats?.by_status?.email_sent || 0}
          </div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label">Riusi cache (7gg)</div>
          <div className="stat-num">{stats?.reused_count ?? "—"}</div>
          <div className="text-small text-muted mt-1">crediti SEOZoom/DFS risparmiati</div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label">Fallite</div>
          <div className="stat-num" style={{color:stats?.by_status?.failed ? "var(--mv-danger)" : "#888"}}>
            {stats?.by_status?.failed || 0}
          </div>
        </div>
      </div>

      {/* Source breakdown */}
      {stats?.by_source && Object.keys(stats.by_source).length > 0 && (
        <div className="card mb-5" style={{padding:14}}>
          <div className="text-small text-muted mb-2">Sorgenti delle richieste</div>
          <div className="flex gap-2" style={{flexWrap:"wrap"}}>
            {Object.entries(stats.by_source).map(([src, n]) => (
              <span key={src} style={{
                background:"#f4f4f5", padding:"4px 12px", borderRadius:99,
                fontSize:13, fontWeight:500,
              }}>
                {SOURCE_LABEL[src] || src} · <strong>{n}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-3" data-testid="filter-bar">
        <Filter size={14} color="var(--mv-text-muted)"/>
        <span className="text-small text-muted">Filtra:</span>
        {["all", "queued", "analyzing", "email_sent", "failed"].map(s => (
          <button key={s} onClick={()=>setFilterStatus(s)}
            data-testid={`filter-${s}`}
            className={"btn btn-sm " + (filterStatus===s ? "btn-primary" : "btn-ghost")}
            style={{padding:"4px 10px",fontSize:12}}>
            {s === "all" ? "Tutte" : STATUS_BADGE[s]?.label || s}
          </button>
        ))}
        <button onClick={()=>setFilterStatus("suspicious")} data-testid="filter-suspicious"
          className={"btn btn-sm " + (filterStatus==="suspicious" ? "btn-primary" : "btn-ghost")}
          style={{padding:"4px 10px",fontSize:12}}>
          ⚠ Dati sospetti ({items.filter(i=>i.quality?.suspicious).length})
        </button>
        <div style={{flex:1}}/>
        <button onClick={load} className="btn btn-ghost btn-sm" data-testid="refresh-btn">
          <RefreshCw size={14}/> Aggiorna
        </button>
      </div>

      {loading ? (
        <div className="card text-center" style={{padding:48}}>
          <Loader2 size={28} className="spin" style={{margin:"0 auto 8px",color:"var(--mv-primary)"}}/>
          <div className="text-muted">Caricamento…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center" style={{padding:48}} data-testid="empty-state">
          <Inbox size={36} color="#cbd5e1" style={{margin:"0 auto 10px"}}/>
          <div className="text-muted">
            {filterStatus === "all"
              ? "Ancora nessun lead inbound. Configura il webhook su WordPress per iniziare a riceverli."
              : "Nessun lead in questo stato."}
          </div>
          {filterStatus === "all" && (
            <button onClick={()=>setShowWebhookHelp(true)} className="btn btn-primary btn-sm mt-3">
              Vedi istruzioni configurazione →
            </button>
          )}
        </div>
      ) : (
        <div className="card" style={{padding:0,overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
          <table className="w-full" style={{borderCollapse:"collapse",fontSize:14}}>
            <thead>
              <tr style={{background:"#fafafa",fontSize:12,textTransform:"uppercase",color:"var(--mv-text-muted)"}}>
                <th style={{textAlign:"left",padding:"10px 12px"}}>Richiedente</th>
                <th style={{textAlign:"left",padding:"10px 12px"}}>Contatti</th>
                <th style={{textAlign:"left",padding:"10px 12px"}}>Sito analizzato</th>
                <th style={{textAlign:"left",padding:"10px 12px"}}>Stato</th>
                <th style={{textAlign:"left",padding:"10px 12px"}}>Sorgente</th>
                <th style={{textAlign:"left",padding:"10px 12px"}}>Ricevuto</th>
                <th style={{padding:"10px 12px",textAlign:"right"}}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => {
                const sb = STATUS_BADGE[it.status] || { bg:"#eee", color:"#444", label:it.status };
                const busyState = busy[it.id];
                const reportUrl = `/analisi/${it.publicToken}`;
                return (
                  <tr key={it.id} data-testid={`row-${it.id}`} style={{borderTop:"1px solid #f0f0f0"}}>
                    <td style={{padding:"12px"}}>
                      <div style={{fontWeight:600}}>{it.firstName} {it.lastName}</div>
                      {it.companyName && <div className="text-small text-muted">{it.companyName}</div>}
                    </td>
                    <td style={{padding:"12px"}}>
                      <div className="text-small" style={{display:"flex",alignItems:"center",gap:6}}>
                        <Mail size={11}/>
                        <a href={`mailto:${it.email}`} style={{color:"var(--mv-primary)"}}>{it.email}</a>
                      </div>
                      {it.phone && (
                        <div className="text-small text-muted" style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
                          <Phone size={11}/> {it.phone}
                        </div>
                      )}
                    </td>
                    <td style={{padding:"12px"}}>
                      <a href={`https://${it.domain}`} target="_blank" rel="noreferrer"
                        style={{color:"var(--mv-primary)",fontWeight:500}}>
                        {it.domain} <ExternalLink size={11} style={{display:"inline"}}/>
                      </a>
                      {it.reusedAnalysis && (
                        <div style={{display:"inline-block",marginLeft:8,background:"#dcfce7",color:"#15803d",padding:"1px 7px",borderRadius:4,fontSize:11,fontWeight:600}}>
                          ♻ Cache
                        </div>
                      )}
                    </td>
                    <td style={{padding:"12px"}}>
                      <span style={{background:sb.bg,color:sb.color,padding:"3px 10px",borderRadius:99,fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>
                        {sb.label}
                      </span>
                      {it.status === "failed" && it.errorMessage && (
                        <div className="text-small text-muted mt-1" title={it.errorMessage}
                          style={{maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          ⚠ {it.errorMessage}
                        </div>
                      )}
                      {it.quality?.suspicious && (
                        <div className="text-small mt-1" data-testid={`quality-badge-${it.id}`}
                          title={`Dati sospetti:\n· ${it.quality.reasons.join("\n· ")}`}
                          style={{color:"#b45309",fontWeight:600,cursor:"help"}}>
                          ⚠ Dati sospetti ({it.quality.reasons.length})
                        </div>
                      )}
                      {it.reanalyzeError && (
                        <div className="text-small mt-1" title={it.reanalyzeError}
                          data-testid={`reanalyze-error-${it.id}`}
                          style={{color:"var(--mv-danger)",maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          ⚠ Rianalisi non riuscita: {it.reanalyzeError}
                        </div>
                      )}
                    </td>
                    <td style={{padding:"12px"}} className="text-small">
                      <div>{SOURCE_LABEL[it.source] || it.source || "—"}</div>
                      {it.utmCampaign && (
                        <div className="text-muted" style={{fontSize:11}}>{it.utmCampaign}</div>
                      )}
                    </td>
                    <td style={{padding:"12px",whiteSpace:"nowrap"}} className="text-small text-muted">
                      <Clock size={11} style={{display:"inline",marginRight:4}}/>
                      {fmtDate(it.createdAt)}
                      {it.emailSentAt && (
                        <div className="text-small" style={{color:"var(--mv-success)",marginTop:2}}>
                          <CheckCircle2 size={11} style={{display:"inline",marginRight:4}}/>
                          inviata {fmtDate(it.emailSentAt)}
                        </div>
                      )}
                    </td>
                    <td style={{padding:"12px",textAlign:"right",whiteSpace:"nowrap"}}>
                      {(it.status === "email_sent" || it.status === "completed") && (
                        <button onClick={()=>window.open(reportUrl, "_blank")}
                          className="btn btn-ghost btn-sm" title="Apri report"
                          data-testid={`btn-view-${it.id}`}>
                          <Globe size={13}/> Report
                        </button>
                      )}
                      {(it.status === "email_sent" || it.status === "failed" || it.status === "completed") && (
                        <button onClick={()=>resend(it.id)} disabled={!!busyState}
                          className="btn btn-ghost btn-sm" title="Reinvia email"
                          data-testid={`btn-resend-${it.id}`}>
                          {busyState === "resending" ? <Loader2 size={13} className="spin"/> : <Send size={13}/>} Reinvia
                        </button>
                      )}
                      <button onClick={()=>reanalyze(it.id)} disabled={!!busyState}
                        className="btn btn-ghost btn-sm" title="Rianalizza il sito (stesso link, nessuna email al cliente)"
                        data-testid={`btn-reanalyze-${it.id}`}>
                        {busyState === "reanalyzing" ? <Loader2 size={13} className="spin"/> : <RefreshCw size={13}/>} Rianalizza
                      </button>
                      <button onClick={()=>remove(it.id)} disabled={!!busyState}
                        className="btn btn-ghost btn-sm" title="Elimina richiesta"
                        data-testid={`btn-delete-${it.id}`}
                        style={{color:"var(--mv-danger)"}}>
                        {busyState === "deleting" ? <Loader2 size={13} className="spin"/> : <Trash2 size={13}/>}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}


function WebhookHelpCard({ onClose }) {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const endpoint = `${apiUrl}/api/public/inbound/request-analysis`;
  const examplePayload = `{
  "firstName": "Mario",
  "lastName": "Rossi",
  "companyName": "Pizzeria Esempio SRL",
  "email": "mario@esempio.it",
  "phone": "+39 333 1234567",
  "website": "https://www.esempio.it",
  "source": "maxvalle.it",
  "utm_source": "google",
  "utm_medium": "cpc",
  "utm_campaign": "analisi-gratuita-2026"
}`;

  const copy = (txt) => {
    navigator.clipboard.writeText(txt).then(
      () => { /* ok */ },
      () => alert("Impossibile copiare. Selezionare manualmente.")
    );
  };

  return (
    <div className="card mb-5" style={{padding:18, borderLeft:"4px solid var(--mv-primary)"}} data-testid="webhook-help-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div style={{fontSize:16, fontWeight:600, marginBottom:6}}>📡 Configurazione webhook WordPress</div>
          <div className="text-small text-muted">
            Configura il form sul tuo WordPress (es. CF7, Gravity Forms, Fluent Forms, WPForms) per inviare una <strong>POST JSON</strong> a questo endpoint.
            La protezione bot è gestita lato WordPress da reCAPTCHA v3, qui ci basta il <strong>secret token</strong> nell'header.
          </div>
        </div>
        <button onClick={onClose} className="btn btn-ghost btn-sm">×</button>
      </div>

      <div className="mt-3">
        <div className="label text-small">URL endpoint</div>
        <div className="flex items-center gap-2 mt-1">
          <code style={{background:"#f4f4f5",padding:"6px 10px",borderRadius:6,flex:1,fontSize:12,wordBreak:"break-all"}}>
            {endpoint}
          </code>
          <button onClick={()=>copy(endpoint)} className="btn btn-ghost btn-sm" data-testid="copy-endpoint">
            <Copy size={12}/> Copia
          </button>
        </div>
      </div>

      <div className="mt-3">
        <div className="label text-small">Header obbligatori</div>
        <pre style={{background:"#f4f4f5",padding:"10px",borderRadius:6,fontSize:12,marginTop:4,overflowX:"auto"}}>
{`Content-Type: application/json
X-Inbound-Secret: <SECRET CONFIGURATO IN backend/.env>`}
        </pre>
        <div className="text-small text-muted mt-1">
          Il secret è in <code>INBOUND_WEBHOOK_SECRET</code> nel file <code>backend/.env</code>. Tienilo riservato.
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between">
          <div className="label text-small">Payload JSON di esempio</div>
          <button onClick={()=>copy(examplePayload)} className="btn btn-ghost btn-sm">
            <Copy size={12}/> Copia
          </button>
        </div>
        <pre style={{background:"#f4f4f5",padding:"10px",borderRadius:6,fontSize:12,marginTop:4,overflowX:"auto"}}>
          {examplePayload}
        </pre>
        <div className="text-small text-muted mt-2">
          <strong>Campi obbligatori:</strong> <code>firstName</code>, <code>lastName</code>, <code>email</code>, <code>website</code>.<br/>
          <strong>Opzionali:</strong> <code>companyName</code>, <code>phone</code>, <code>source</code>, tutti gli <code>utm_*</code>.<br/>
          <strong>Honeypot:</strong> aggiungi un campo <code>website_confirm</code> nascosto nel form per filtrare bot — se compilato, la richiesta viene scartata silenziosamente.
        </div>
      </div>

      <div className="mt-3" style={{background:"#fff7ed",padding:12,borderRadius:6}}>
        <div className="text-small">
          <strong>💡 Risposte attese:</strong>
          <ul style={{margin:"6px 0 0 18px"}}>
            <li><code>200 {`{ "status": "queued", "publicToken": "...", "estimated_seconds": 120 }`}</code> → analisi avviata, mail tra 1-2 min</li>
            <li><code>200 {`{ "status": "completed_reused", "publicToken": "...", "estimated_seconds": 5 }`}</code> → report già disponibile (cache 7 giorni), mail immediata</li>
            <li><code>200 {`{ "status": "received" }`}</code> → honeypot trigger (bot)</li>
            <li><code>401</code> → secret invalido o mancante</li>
            <li><code>400</code> → dato non valido (es. URL malformato)</li>
            <li><code>422</code> → campi obbligatori mancanti</li>
          </ul>
        </div>
      </div>

      <div className="mt-3 text-small text-muted">
        <strong>Anti-abuso:</strong> protezione reCAPTCHA v3 lato WordPress + dedupe automatico per dominio (7 giorni, riusa report esistente). Le richieste duplicate ricevono comunque la mail.
      </div>
    </div>
  );
}
